import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './EditActiveChannelsModal.scss';

export const ACTIVE_CHANNEL_IDS = [
  'blogs',
  'commentsArticles',
  'commentsFacebook',
  'commentsYoutube',
  'forums',
  'portals',
  'tumblr',
  'youtube',
  'bluesky',
  'commentsBluesky',
  'commentsInstagram',
  'facebook',
  'instagram',
  'complaints',
  'twitter',
  'zapier',
] as const;

export type ActiveChannelsState = Record<(typeof ACTIVE_CHANNEL_IDS)[number], boolean>;

interface ChannelOption {
  id: (typeof ACTIVE_CHANNEL_IDS)[number];
  label: string;
}

interface EditActiveChannelsModalProps {
  isOpen: boolean;
  selectedChannels: ActiveChannelsState;
  onChange: (nextChannels: ActiveChannelsState) => void;
  onFinish?: () => void;
  onClose: () => void;
}

const getOptionClassName = (isActive: boolean): string =>
  `new-setup__channels-modal-option ${isActive ? 'new-setup__channels-modal-option--active' : 'new-setup__channels-modal-option--inactive'}`;

const getSwitchClassName = (isActive: boolean): string =>
  `new-setup__channels-modal-switch ${isActive ? 'new-setup__channels-modal-switch--active' : 'new-setup__channels-modal-switch--inactive'}`;

export const EditActiveChannelsModal: React.FC<EditActiveChannelsModalProps> = ({
  isOpen,
  selectedChannels,
  onChange,
  onFinish,
  onClose,
}) => {
  const { t } = useTranslation();

  const leftColumn: ChannelOption[] = [
    { id: 'blogs', label: t('newSetup.channelsModal.options.blogs') },
    { id: 'commentsArticles', label: t('newSetup.channelsModal.options.commentsArticles') },
    { id: 'commentsFacebook', label: t('newSetup.channelsModal.options.commentsFacebook') },
    { id: 'commentsYoutube', label: t('newSetup.channelsModal.options.commentsYoutube') },
    { id: 'forums', label: t('newSetup.channelsModal.options.forums') },
    { id: 'portals', label: t('newSetup.channelsModal.options.portals') },
    { id: 'tumblr', label: t('newSetup.channelsModal.options.tumblr') },
    { id: 'youtube', label: t('newSetup.channelsModal.options.youtube') },
  ];

  const rightColumn: ChannelOption[] = [
    { id: 'bluesky', label: t('newSetup.channelsModal.options.bluesky') },
    { id: 'commentsBluesky', label: t('newSetup.channelsModal.options.commentsBluesky') },
    { id: 'commentsInstagram', label: t('newSetup.channelsModal.options.commentsInstagram') },
    { id: 'facebook', label: t('newSetup.channelsModal.options.facebook') },
    { id: 'instagram', label: t('newSetup.channelsModal.options.instagram') },
    { id: 'complaints', label: t('newSetup.channelsModal.options.complaints') },
    { id: 'twitter', label: t('newSetup.channelsModal.options.twitter') },
    { id: 'zapier', label: t('newSetup.channelsModal.options.zapier') },
  ];

  const activeCount = useMemo(
    () => ACTIVE_CHANNEL_IDS.filter((channelId) => selectedChannels[channelId]).length,
    [selectedChannels],
  );

  if (!isOpen) {
    return null;
  }

  const toggleChannel = (channelId: (typeof ACTIVE_CHANNEL_IDS)[number]) => {
    onChange({
      ...selectedChannels,
      [channelId]: !selectedChannels[channelId],
    });
  };

  const handleFinish = () => {
    onFinish?.();
    onClose();
  };

  const renderOption = (option: ChannelOption) => {
    const isActive = selectedChannels[option.id];

    return (
      <button
        key={option.id}
        type="button"
        className={getOptionClassName(isActive)}
        onClick={() => toggleChannel(option.id)}
        aria-pressed={isActive}
        aria-label={t('newSetup.channelsModal.toggleAria', { channel: option.label })}
      >
        <span className="new-setup__channels-modal-option-label">{option.label}</span>
        <span className={getSwitchClassName(isActive)} aria-hidden="true">
          <span className="new-setup__channels-modal-switch-knob" />
        </span>
      </button>
    );
  };

  return (
    <div className="new-setup__modal-overlay">
      <dialog open className="new-setup__channels-modal" aria-labelledby="new-setup-channels-modal-title">
        <header className="new-setup__channels-modal-header">
          <button
            type="button"
            className="new-setup__channels-modal-close"
            onClick={onClose}
            aria-label={t('newSetup.channelsModal.close')}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>

          <h2 id="new-setup-channels-modal-title" className="new-setup__channels-modal-title">
            {t('newSetup.channelsModal.title')}
          </h2>
          <p className="new-setup__channels-modal-subtitle">{t('newSetup.channelsModal.subtitle')}</p>
        </header>

        <div className="new-setup__channels-modal-content">
          <div className="new-setup__channels-modal-divider" />

          <div className="new-setup__channels-modal-copy">
            <h3 className="new-setup__channels-modal-copy-title">{t('newSetup.channelsModal.heading')}</h3>
            <p className="new-setup__channels-modal-copy-text">{t('newSetup.channelsModal.description')}</p>
          </div>

          <div className="new-setup__channels-modal-grid">
            <div className="new-setup__channels-modal-column">{leftColumn.map(renderOption)}</div>
            <div className="new-setup__channels-modal-column">{rightColumn.map(renderOption)}</div>
          </div>

          <p className="new-setup__channels-modal-count">
            {t('newSetup.channelsModal.activeCount', { active: activeCount, total: ACTIVE_CHANNEL_IDS.length })}
          </p>
        </div>

        <footer className="new-setup__channels-modal-footer">
          <button type="button" className="new-setup__channels-modal-confirm" onClick={handleFinish}>
            {t('newSetup.channelsModal.finish')}
          </button>
        </footer>
      </dialog>
    </div>
  );
};
